/** Mirrors backend `ai.parse.*` and request `@Size` limits. */
export const AI_PARSE_MIN_CHARS = 10
export const AI_PARSE_BLOCK_MAX_CHARS = 5000
export const AI_PARSE_PROGRAM_MAX_CHARS = 10000
export const AI_PARSE_REPARSE_COMMENT_MAX_CHARS = 1000

export type ParseTextValidationError = 'empty' | 'tooShort' | 'tooLong'

export function validateParseRawText(
  text: string,
  maxChars: number,
): ParseTextValidationError | null {
  const trimmed = text.trim()
  if (!trimmed) return 'empty'
  if (trimmed.length < AI_PARSE_MIN_CHARS) return 'tooShort'
  if (text.length > maxChars) return 'tooLong'
  return null
}

export function validateReparseComment(text: string): ParseTextValidationError | null {
  const trimmed = text.trim()
  if (!trimmed) return 'empty'
  if (text.length > AI_PARSE_REPARSE_COMMENT_MAX_CHARS) return 'tooLong'
  return null
}

export function isParseInputTooShortMessage(message: string | undefined): boolean {
  return !!message && message.toLowerCase().includes('too short')
}

export function isParseInputTooLargeMessage(message: string | undefined): boolean {
  const m = message?.toLowerCase() ?? ''
  return m.includes('too large') || m.includes('too long')
}

type TranslateFn = (key: string, params?: Record<string, string | number>) => string

export function parseTextValidationMessage(
  error: ParseTextValidationError,
  translate: TranslateFn,
  maxChars: number,
): string {
  switch (error) {
    case 'empty':
      return translate('aiParse.validation.textRequired')
    case 'tooShort':
      return translate('aiParse.validation.textTooShort', { min: AI_PARSE_MIN_CHARS })
    case 'tooLong':
      return translate('aiParse.validation.textTooLong', { max: maxChars })
  }
}

export function reparseCommentValidationMessage(
  error: ParseTextValidationError,
  translate: TranslateFn,
): string {
  switch (error) {
    case 'empty':
      return translate('aiParse.validation.correctionRequired')
    case 'tooLong':
      return translate('aiParse.validation.correctionTooLong', {
        max: AI_PARSE_REPARSE_COMMENT_MAX_CHARS,
      })
    default:
      return translate('aiParse.validation.correctionRequired')
  }
}

export function mapParseApiErrorMessage(
  message: string | undefined,
  translate: TranslateFn,
  _maxChars: number,
  fallback: string,
): string {
  if (isParseInputTooShortMessage(message)) {
    return translate('aiParse.validation.textTooShort', { min: AI_PARSE_MIN_CHARS })
  }
  if (isParseInputTooLargeMessage(message)) {
    return translate('aiParse.validation.inputTooLarge')
  }
  return message || fallback
}
